(() => {
  if (window.BrandFilmLoaded) return;
  window.BrandFilmLoaded = true;

  const track = (name, handle) => {
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('set', 'brand_film', handle);
        window.clarity('event', name);
      }
    } catch (e) {}
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, { film: handle });
      }
    } catch (e) {}
  };

  const canHls = (video) => video.canPlayType('application/vnd.apple.mpegurl') !== '';

  const pickSrc = (video, size) => {
    const d = video.dataset;
    if (d.hls && canHls(video)) return d.hls;
    if (size === 'lg') return d.lg || d.sm || '';
    return d.sm || d.lg || '';
  };

  const cappedVariant = async (masterUrl, maxHeight) => {
    try {
      const base = new URL(masterUrl, window.location.href);
      const res = await fetch(base.href, { credentials: 'omit' });
      if (!res.ok) return '';
      const lines = (await res.text()).split(/\r?\n/);
      let best = null;
      lines.forEach((line, i) => {
        const m = /^#EXT-X-STREAM-INF:.*RESOLUTION=(\d+)x(\d+)/.exec(line);
        const uri = (lines[i + 1] || '').trim();
        if (!m || !uri || uri.startsWith('#')) return;
        const h = parseInt(m[2], 10);
        if (h <= maxHeight && (!best || h > best.h)) best = { h, uri };
      });
      return best ? new URL(best.uri, base).href : '';
    } catch (e) {
      return '';
    }
  };

  const autoplayAllowed = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    const c = navigator.connection;
    if (c && (c.saveData || /2g|3g/.test(c.effectiveType || ''))) return false;
    return true;
  };

  const afterLoad = (fn) => {
    const run = () => ('requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 2500 }) : setTimeout(fn, 300));
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  };

  const storeToday = (offsetMin) => {
    const d = new Date(Date.now() + offsetMin * 60000);
    const p = (v) => String(v).padStart(2, '0');
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
  };

  const selectSlides = () => {
    document.querySelectorAll('[data-bf-section]').forEach((section) => {
      const slides = [...section.querySelectorAll('brand-film')];
      if (!slides.length) return;
      const today = storeToday(parseInt(section.dataset.tzOffset || '0', 10));
      const live = slides.find((s) => (!s.dataset.from || s.dataset.from <= today) && (!s.dataset.until || today <= s.dataset.until));
      slides.forEach((s) => {
        const show = s === live;
        if (!show && !s.hidden && typeof s.deactivate === 'function') s.deactivate();
        s.hidden = !show;
        if (show && typeof s.activate === 'function') s.activate();
      });
      section.hidden = !live;
    });
  };

  const scheduleMidnight = () => {
    const section = document.querySelector('[data-bf-section]');
    if (!section) return;
    const offset = parseInt(section.dataset.tzOffset || '0', 10) * 60000;
    const storeNow = Date.now() + offset;
    const nextMidnight = Math.floor(storeNow / 86400000 + 1) * 86400000;
    setTimeout(() => {
      selectSlides();
      scheduleMidnight();
    }, Math.min(nextMidnight - storeNow + 5000, 2147483000));
  };

  selectSlides();
  scheduleMidnight();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') selectSlides();
  });

  class BrandFilm extends HTMLElement {
    connectedCallback() {
      this.activate();
      if (this.initialized) afterLoad(() => this.observe());
    }

    activate() {
      if (this.hidden || this.initialized) return;
      this.initialized = true;
      this.video = this.querySelector('video');
      this.playBtn = this.querySelector('[data-bf-play]');
      this.soundBtn = this.querySelector('[data-bf-sound]');
      this.bar = this.querySelector('.bf-film__progress i');
      this.handle = this.dataset.handle || '';
      this.started = false;
      this.userPaused = false;
      this.playBtn.addEventListener('click', () => this.togglePlay());
      this.soundBtn.addEventListener('click', () => this.toggleSound());
      ['play', 'pause', 'volumechange'].forEach((t) => this.video.addEventListener(t, () => this.sync()));
      this.video.addEventListener('playing', () => this.classList.add('is-playing'));
      this.video.addEventListener('timeupdate', () => {
        const d = this.video.duration;
        if (this.bar && d) this.bar.style.transform = `scaleX(${this.video.currentTime / d})`;
        if (d && !this.completed && this.video.currentTime > d - 0.6) {
          this.completed = true;
          track('brand_film_complete', this.handle);
        }
      });
      this.sync();
      afterLoad(() => this.observe());
    }

    deactivate() {
      this.wantPlay = false;
      if (this.video && !this.video.paused) this.video.pause();
    }

    disconnectedCallback() {
      this.wantPlay = false;
      if (this.io) {
        this.io.disconnect();
        this.io = null;
      }
      if (this.video && !this.video.paused) this.video.pause();
    }

    observe() {
      if (this.io || !this.isConnected || this.closest('[hidden]') || !('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (entry.isIntersecting) {
          if (!this.userPaused && (this.started || autoplayAllowed())) this.play();
        } else {
          this.wantPlay = false;
          if (!this.video.paused) this.video.pause();
        }
      }, { threshold: 0.35 });
      this.io.observe(this);
    }

    load() {
      if (!this.loading) this.loading = this.resolveSrc();
      return this.loading;
    }

    async resolveSrc() {
      if (this.video.getAttribute('src')) return;
      const phone = !window.matchMedia('(min-width: 750px)').matches;
      let src = pickSrc(this.video, phone ? 'sm' : 'lg');
      if (phone && src && src === this.video.dataset.hls) {
        const cellular = navigator.connection && navigator.connection.type === 'cellular';
        src = (await cappedVariant(src, cellular ? 480 : 720)) || src;
      }
      if (src && !this.video.getAttribute('src')) this.video.src = src;
    }

    async play() {
      this.wantPlay = true;
      await this.load();
      if (!this.wantPlay || this.userPaused || !this.isConnected || this.closest('[hidden]') || this.inView === false) return;
      if (!this.video.paused) return;
      const p = this.video.play();
      if (p && p.then) {
        p.then(() => {
          this.classList.remove('is-blocked');
          if (!this.started) {
            this.started = true;
            track('brand_film_play', this.handle);
          }
        }).catch(() => this.classList.add('is-blocked'));
      }
    }

    togglePlay() {
      if (this.video.paused) {
        this.userPaused = false;
        this.play();
      } else {
        this.userPaused = true;
        this.wantPlay = false;
        this.video.pause();
      }
    }

    toggleSound() {
      this.video.muted = !this.video.muted;
      if (!this.video.muted) {
        track('brand_film_sound_on', this.handle);
        if (this.video.paused) {
          this.userPaused = false;
          this.play();
        }
      }
    }

    sync() {
      const paused = this.video.paused;
      const muted = this.video.muted;
      this.classList.toggle('is-paused', paused);
      this.classList.toggle('is-muted', muted);
      this.playBtn.setAttribute('aria-label', paused ? this.playBtn.dataset.labelPlay : this.playBtn.dataset.labelPause);
      this.soundBtn.setAttribute('aria-pressed', String(!muted));
      this.soundBtn.setAttribute('aria-label', muted ? this.soundBtn.dataset.labelOn : this.soundBtn.dataset.labelOff);
    }
  }

  class BrandFilmCard extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        if (this.watch) this.watch.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
        return;
      }
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.video = this.dialog.querySelector('video');
      this.handle = this.dataset.handle || '';
      this.opener = this.querySelector('[data-bf-open]');
      this.opener.addEventListener('click', () => this.open());
      this.dialog.querySelector('[data-bf-close]').addEventListener('click', () => this.close());
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
        }
      });
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      });
      this.watch = new MutationObserver(() => { if (!this.dialog.open) this.cleanup(); });
      this.watch.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
      const shop = this.dialog.querySelector('[data-bf-shop]');
      if (shop) shop.addEventListener('click', () => track('brand_film_shop_click', this.handle));
      this.video.addEventListener('ended', () => track('brand_film_complete', this.handle));
    }

    close() {
      if (this.dialog.open) this.dialog.close();
      this.cleanup();
    }

    cleanup() {
      if (this.video && !this.video.paused) this.video.pause();
      if (!this.locked) return;
      this.locked = false;
      document.documentElement.classList.remove('bf-lock');
      if (this.restoreFocus && this.opener && this.isConnected) this.opener.focus({ preventScroll: true });
      this.restoreFocus = false;
    }

    disconnectedCallback() {
      if (this.watch) this.watch.disconnect();
      this.restoreFocus = false;
      this.cleanup();
    }

    open() {
      const src = this.video.getAttribute('src') || pickSrc(this.video, 'lg');
      if (typeof this.dialog.showModal !== 'function') {
        if (src) window.location.href = src;
        return;
      }
      if (!this.video.getAttribute('src') && src) this.video.src = src;
      if (!this.video.getAttribute('poster') && this.video.dataset.poster) this.video.poster = this.video.dataset.poster;
      this.restoreFocus = true;
      this.dialog.showModal();
      this.locked = true;
      document.documentElement.classList.add('bf-lock');
      this.video.muted = false;
      const p = this.video.play();
      if (p && p.catch) p.catch(() => {});
      track('brand_film_open', this.handle);
    }
  }

  if (!customElements.get('brand-film')) customElements.define('brand-film', BrandFilm);
  if (!customElements.get('brand-film-card')) customElements.define('brand-film-card', BrandFilmCard);
})();
